import { memo, useMemo } from 'react';
import { Platform, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import * as FilterBarStyles from '../../../components/FilterBar/style';
import { FilterBar } from '../../../components/FilterBar';
import { FilterChipRow } from '../../../components/FilterBar/FilterChipRow';
import { useFilterPickerAnchor } from '../../../components/FilterBar/useFilterPickerAnchor';
import { FILTER_BAR_MAX_INLINE } from '../../../components/FilterBar/chipLayout';
import { FilterPickerDrawer } from '../../../components/FilterPickerDrawer';
import { borderRadiusButtons } from '../../../config/data';
import THEME from '../../../config/theme';
import { useCustomTheme } from '../../../contexts/useCustomTheme';
import { removeSansSerif } from '../../../utils/validators';
import {
  PRODUCT_VITRINE_FILTER_ALL,
  PRODUCT_VITRINE_FILTER_MINE,
} from './productVitrineFilters';

const HEADER_GUTTER = 11;

const VIRTUAL_FILTER_VALUES = new Set([
  PRODUCT_VITRINE_FILTER_ALL,
  PRODUCT_VITRINE_FILTER_MINE,
]);

export const ProductVitrineFilterBar = memo(
  ({
    filters,
    selected,
    onSelect,
    onViewAllContent,
    onReorderCategories,
    colors,
    fonts,
    fontSize: fontSizeProp,
    overlay = false,
  }) => {
    const { dropdownRef, anchor, fallbackTop, visible, open, close } =
      useFilterPickerAnchor();
    const { customInfo, customColor, customFont } = useCustomTheme();
    const baseFontSize = fontSizeProp ?? customFont?.fontMD ?? THEME?.FONTSIZE?.MEDIUM;
    const chipFontSize = baseFontSize;
    const chipBorderRadius =
      customInfo?.borderRadius != null ? customInfo.borderRadius : borderRadiusButtons;
    const chipFont =
      fonts ??
      (customFont?.fontFamilyText
        ? removeSansSerif(customFont.fontFamilyText)
        : THEME.FONTFAMILY.REGULAR);
    const chipColors = colors ?? {
      activeButton: customColor?.button?.backgroundApp ?? THEME.COLORS.PRIMARY_800,
      inactiveButton: customColor?.iconsMain?.icons ?? THEME.COLORS.ICON_MAIN,
      activeText: customColor?.button?.textApp ?? THEME.COLORS.TEXT_BUTTON,
      background: customColor?.background?.app ?? THEME.COLORS.BACKGROUND_MAIN,
      text: customColor?.text?.app ?? THEME.COLORS.TEXT_MAIN,
    };

    const { virtualFilters, categoryFilters, inlineFilters, drawerItems, showDropdown } =
      useMemo(() => {
        const virtual = filters.filter((f) => VIRTUAL_FILTER_VALUES.has(f.value));
        const categories = filters.filter((f) => !VIRTUAL_FILTER_VALUES.has(f.value));

        const todosFilter = filters.find((f) => f.value === PRODUCT_VITRINE_FILTER_ALL);
        const withoutTodos = filters.filter((f) => f.value !== PRODUCT_VITRINE_FILTER_ALL);
        const inline = withoutTodos.slice(0, FILTER_BAR_MAX_INLINE);
        const overflowCategories = withoutTodos.slice(FILTER_BAR_MAX_INLINE);
        const drawer = [
          ...(todosFilter ? [todosFilter] : []),
          ...overflowCategories,
        ].map((f) => ({ label: f.label, value: f.value }));

        return {
          virtualFilters: virtual,
          categoryFilters: categories,
          inlineFilters: inline,
          drawerItems: drawer,
          showDropdown: drawer.length > 0 || !!onViewAllContent,
        };
      }, [filters, onViewAllContent]);

    const canReorderCategories =
      !!onReorderCategories && Platform.OS === 'web' && categoryFilters.length > 0;

    const renderChip = (item) => {
      const active = item.value === selected;
      return (
        <FilterBarStyles.Chip
          key={item.value}
          borderRadiusButton={chipBorderRadius}
          onPress={() => onSelect(item.value)}
          borderColor={`${active ? chipColors.activeButton : chipColors.inactiveButton}90`}
          backgroundColor={active ? chipColors.activeButton : 'transparent'}
        >
          <FilterBarStyles.Text
            fontWeight={active ? 'bold' : 'normal'}
            fontFamily={chipFont}
            color={active ? chipColors.activeText : chipColors.inactiveButton}
            fontSize={chipFontSize}
          >
            {item.label}
          </FilterBarStyles.Text>
        </FilterBarStyles.Chip>
      );
    };

    if (canReorderCategories) {
      const categoryFiltersWithIds = categoryFilters.map((filter) => ({
        ...filter,
        id: filter.value,
      }));

      const adminFilterRow = (
        <>
          {virtualFilters.length > 0 ? (
            <FilterChipRow paddingHorizontal={overlay ? HEADER_GUTTER : 0}>
              {virtualFilters.map(renderChip)}
            </FilterChipRow>
          ) : null}
          <FilterBar
            filters={categoryFiltersWithIds}
            selected={selected}
            onSelect={onSelect}
            onReorder={onReorderCategories}
            colors={chipColors.inactiveButton}
            fonts={chipFont}
            showClear={false}
            extraMenuActions={
              onViewAllContent
                ? [{ label: 'Ver todos os conteúdos', onPress: onViewAllContent }]
                : []
            }
          />
        </>
      );

      if (!overlay) {
        return <View style={{ paddingVertical: 8 }}>{adminFilterRow}</View>;
      }

      return (
        <FilterBarStyles.Container pointerEvents="box-none">
          {adminFilterRow}
        </FilterBarStyles.Container>
      );
    }

    const selectedOverflowFilter = drawerItems.find((item) => item.value === selected);

    const overflowActive =
      selected === PRODUCT_VITRINE_FILTER_ALL || !!selectedOverflowFilter;

    const overflowLabel = selectedOverflowFilter?.label ?? 'Todos';

    const dropdownChip = showDropdown ? (
      <View ref={dropdownRef} collapsable={false}>
        <FilterBarStyles.Chip
          borderRadiusButton={chipBorderRadius}
          onPress={open}
          borderColor={`${overflowActive ? chipColors.activeButton : chipColors.inactiveButton}90`}
          backgroundColor={overflowActive ? chipColors.activeButton : 'transparent'}
        >
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
            <FilterBarStyles.Text
              fontWeight={overflowActive ? 'bold' : 'normal'}
              fontFamily={chipFont}
              color={overflowActive ? chipColors.activeText : chipColors.inactiveButton}
              fontSize={chipFontSize}
            >
              {overflowLabel}
            </FilterBarStyles.Text>
            <Ionicons
              name="chevron-down"
              size={12}
              color={overflowActive ? chipColors.activeText : chipColors.inactiveButton}
            />
          </View>
        </FilterBarStyles.Chip>
      </View>
    ) : null;

    const chipRow = (
      <FilterChipRow paddingHorizontal={overlay ? HEADER_GUTTER : 0}>
        {inlineFilters.map(renderChip)}
        {dropdownChip}
      </FilterChipRow>
    );

    const drawer = (
      <FilterPickerDrawer
        visible={visible}
        onClose={close}
        anchor={anchor}
        fallbackTop={fallbackTop}
        items={drawerItems}
        onSelect={(value) => value != null && onSelect(value)}
        actions={
          onViewAllContent
            ? [{ label: 'Ver todos os conteúdos', onPress: onViewAllContent }]
            : []
        }
        colors={{
          background: chipColors.background,
          text: chipColors.text,
          inactiveButton: chipColors.inactiveButton,
        }}
        fonts={chipFont}
        fontSize={chipFontSize}
      />
    );

    if (!overlay) {
      return (
        <>
          <View style={{ paddingVertical: 8 }}>{chipRow}</View>
          {drawer}
        </>
      );
    }

    return (
      <>
        <FilterBarStyles.Container pointerEvents="box-none">{chipRow}</FilterBarStyles.Container>
        {drawer}
      </>
    );
  }
);

ProductVitrineFilterBar.displayName = 'ProductVitrineFilterBar';

export default ProductVitrineFilterBar;
